import { useCallback, useEffect, useReducer, useRef } from "react";

const { API_ROOT } = runConfig;

export interface QuotaLevel {
  count: number;
  limit: number;
}

interface QuotaState {
  initialized: boolean;
  allowed: boolean;
  reason: "institution_quota_exceeded" | "no_quota_configured" | null;
  user: QuotaLevel | null;
  institution: QuotaLevel | null;
  error: string | null;
}

type QuotaAction =
  | { type: "initSuccess"; allowed: boolean; reason: QuotaState["reason"]; user: QuotaLevel; institution: QuotaLevel }
  | { type: "initError" }
  | { type: "fetchSuccess"; allowed: boolean; reason: QuotaState["reason"]; user: QuotaLevel; institution: QuotaLevel }
  | { type: "fetchError" }
  | { type: "consumeSuccess"; user: QuotaLevel; institution: QuotaLevel }
  | { type: "consumeDenied"; reason: QuotaState["reason"]; user: QuotaLevel; institution: QuotaLevel }
  | { type: "consumeError" }
  | { type: "reset" };

const initialState: QuotaState = {
  initialized: false,
  allowed: false,
  reason: null,
  user: null,
  institution: null,
  error: null,
};

function quotaReducer(state: QuotaState, action: QuotaAction): QuotaState {
  switch (action.type) {
    case "initSuccess":
      return {
        ...state,
        initialized: true,
        allowed: action.allowed,
        reason: action.reason,
        user: action.user,
        institution: action.institution,
        error: null,
      };
    case "initError":
      return {
        ...state,
        initialized: false,
        allowed: false,
        error: "connect_error",
      };
    case "fetchSuccess":
      return {
        ...state,
        allowed: action.allowed,
        reason: action.reason,
        user: action.user,
        institution: action.institution,
      };
    case "fetchError":
      // Fail-open after initialization — leave allowed at last known value
      return state;
    case "consumeSuccess":
      return {
        ...state,
        user: action.user,
        institution: action.institution,
      };
    case "consumeDenied":
      return {
        ...state,
        allowed: false,
        reason: action.reason,
        user: action.user,
        institution: action.institution,
      };
    case "consumeError":
      // Fail-open — leave state unchanged
      return state;
    case "reset":
      return initialState;
    default:
      return state;
  }
}

interface QuotaStatusResponse {
  allowed: boolean;
  reason: QuotaState["reason"];
  user: QuotaLevel;
  institution: QuotaLevel;
}

export function useQuotaStatus(isOpen: boolean) {
  const [state, dispatch] = useReducer(quotaReducer, initialState);
  const initializedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const csrfTokenRef = useRef<string | null>(null);

  const fetchCsrfToken = useCallback((): Promise<string> => {
    if (csrfTokenRef.current) return Promise.resolve(csrfTokenRef.current);
    return fetch(`${API_ROOT}/export/token`, {
      method: "GET",
      mode: "cors",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((result: { token: string }) => {
        csrfTokenRef.current = result.token;
        return result.token;
      });
  }, []);

  const fetchStatus = useCallback(() => {
    const isFirstFetch = !initializedRef.current;

    fetch(`${API_ROOT}/sa/quota/ai-chat/status`, {
      method: "GET",
      mode: "cors",
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json();
      })
      .then((result: QuotaStatusResponse) => {
        if (isFirstFetch) {
          initializedRef.current = true;
          dispatch({
            type: "initSuccess",
            allowed: result.allowed,
            reason: result.reason,
            user: result.user,
            institution: result.institution,
          });
        } else {
          dispatch({
            type: "fetchSuccess",
            allowed: result.allowed,
            reason: result.reason,
            user: result.user,
            institution: result.institution,
          });
        }
      })
      .catch(() => {
        if (isFirstFetch) {
          dispatch({ type: "initError" });
        } else {
          dispatch({ type: "fetchError" });
        }
      });
  }, []);

  const recordUsage = useCallback(() => {
    fetchCsrfToken()
      .then((token) => {
        const formData = new FormData();
        formData.append("_csrf_token", token);
        return fetch(`${API_ROOT}/sa/quota/ai-chat/consume`, {
          method: "POST",
          mode: "cors",
          credentials: "include",
          body: formData,
        });
      })
      .then((res) =>
        res.json().then((result: QuotaStatusResponse) => {
          if (res.status === 429) {
            dispatch({
              type: "consumeDenied",
              reason: result.reason,
              user: result.user,
              institution: result.institution,
            });
          } else {
            dispatch({
              type: "consumeSuccess",
              user: result.user,
              institution: result.institution,
            });
          }
          // Re-fetch status after consume so limits always reflect the latest server config
          fetchStatus();
        })
      )
      .catch(() => dispatch({ type: "consumeError" }));
  }, [fetchCsrfToken, fetchStatus]);

  useEffect(() => {
    if (isOpen) {
      initializedRef.current = false;
      fetchStatus();
      intervalRef.current = setInterval(fetchStatus, 30_000);
    } else {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      initializedRef.current = false;
      dispatch({ type: "reset" });
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, fetchStatus]);

  const { initialized, allowed, reason, user, institution, error } = state;

  const canSend = initialized && allowed;
  const institutionRemaining = institution
    ? Math.max(0, institution.limit - institution.count)
    : 0;

  return {
    initialized,
    allowed,
    reason,
    user,
    institution,
    error,
    canSend,
    institutionRemaining,
    fetchStatus,
    recordUsage,
  };
}
